import { apiClient } from './apiClient';
import type { ServiceTicket } from './customerSuccessTicketsApi';

export type SupportDeskQueueCounts = {
  needsAttention: number;
  mine: number;
  unassigned: number;
  grouped: number;
  archived: number;
};

export type SupportDeskQueueItem = {
  id: string;
  ticketCode: string;
  title: string;
  previewText?: string | null;
  status: string;
  priority: string;
  sourceChannel: string;
  supportChannelConfigId?: string | null;
  sourceChannelName?: string | null;
  conversationOwner: string;
  conversationOwnerLabel: string;
  alertOccurrenceCount?: number;
  companyId: string;
  companyName: string;
  contactName?: string | null;
  assignedToUserId?: string | null;
  assignedToUserName?: string | null;
  assignedTeamId?: string | null;
  assignedTeamName?: string | null;
  problemId?: string | null;
  problemCode?: string | null;
  serviceCatalogItemId?: string | null;
  serviceCatalogItemName?: string | null;
  tags?: string | null;
  isUnassigned: boolean;
  isGrouped: boolean;
  waitingForFirstResponse: boolean;
  isArchived: boolean;
  slaState: string;
  slaResponseDueAtUtc?: string | null;
  slaResolutionDueAtUtc?: string | null;
  nextActionCode: string;
  nextActionLabel: string;
  updatedAtUtc: string;
  createdAtUtc: string;
};

export type SupportDeskQueueResult = {
  view: string;
  counts: SupportDeskQueueCounts;
  availableTags: string[];
  items: SupportDeskQueueItem[];
  totalCount: number;
  page: number;
  pageSize: number;
  nextCursor?: string | null;
};

export type SupportDeskView =
  | 'needs_attention'
  | 'mine'
  | 'unassigned'
  | 'grouped'
  | 'archived';

/** Escopo do filtro Fila na mesa: uma fila específica ou todas (com busca). */
export type SupportDeskQueueFilter = SupportDeskView | 'all';

export async function listSupportDeskQueue(params?: {
  view?: SupportDeskView | SupportDeskQueueFilter | string;
  search?: string;
  priority?: string;
  teamId?: string;
  slaState?: string;
  tag?: string;
  catalogItemId?: string;
  cursor?: string;
  pageSize?: number;
}): Promise<SupportDeskQueueResult> {
  const response = await apiClient.get<SupportDeskQueueResult>('/v1/customer-success/desk/queue', {
    params,
  });
  return response.data;
}

export type SupportDeskActivity = {
  changedAtUtc: string;
};

export async function getSupportDeskActivity(): Promise<SupportDeskActivity> {
  const response = await apiClient.get<SupportDeskActivity>('/v1/customer-success/desk/activity');
  return response.data;
}

export async function getSupportDeskTicket(
  id: string,
  options?: { signal?: AbortSignal },
): Promise<ServiceTicket> {
  const response = await apiClient.get<ServiceTicket>(`/v1/customer-success/desk/tickets/${id}`, {
    signal: options?.signal,
  });
  return response.data;
}

export async function claimSupportDeskTicket(id: string): Promise<ServiceTicket> {
  const response = await apiClient.post<ServiceTicket>(`/v1/customer-success/desk/tickets/${id}/claim`);
  return response.data;
}

export async function returnSupportDeskTicketToAssistant(id: string): Promise<ServiceTicket> {
  const response = await apiClient.post<ServiceTicket>(
    `/v1/customer-success/desk/tickets/${id}/return-to-assistant`,
  );
  return response.data;
}

export async function archiveSupportDeskTicket(
  id: string,
  reason: string,
): Promise<ServiceTicket> {
  const response = await apiClient.post<ServiceTicket>(
    `/v1/customer-success/desk/tickets/${id}/archive`,
    { reason },
  );
  return response.data;
}

export async function reopenSupportDeskTicket(
  id: string,
  reason?: string,
): Promise<ServiceTicket> {
  const response = await apiClient.post<ServiceTicket>(
    `/v1/customer-success/desk/tickets/${id}/reopen`,
    { reason: reason?.trim() || undefined },
  );
  return response.data;
}

export type SupportDeskSendSurveyResult = {
  enqueued: boolean;
  message: string;
  surveyInviteId?: string | null;
  surveyType: string;
};

export async function sendSupportDeskSurvey(
  id: string,
  surveyType: 'Csat' | 'Nps',
): Promise<SupportDeskSendSurveyResult> {
  const response = await apiClient.post<SupportDeskSendSurveyResult>(
    `/v1/customer-success/desk/tickets/${id}/send-survey`,
    { surveyType },
  );
  return response.data;
}

export async function suggestSupportDeskArchiveReason(
  id: string,
  hint?: string,
): Promise<{ reason: string }> {
  const response = await apiClient.post<{ reason: string }>(
    `/v1/customer-success/desk/tickets/${id}/suggest-archive-reason`,
    { hint: hint?.trim() || undefined },
  );
  return response.data;
}

export type SupportDeskSuggestPurpose =
  | 'ArchiveReason'
  | 'ReopenReason'
  | 'Reply'
  | 'InternalNote'
  | 'Broadcast';

export async function suggestSupportDeskMessage(
  id: string,
  purpose: SupportDeskSuggestPurpose,
  hint?: string,
): Promise<{ text: string; purpose: string }> {
  const response = await apiClient.post<{ text: string; purpose: string }>(
    `/v1/customer-success/desk/tickets/${id}/suggest-message`,
    {
      purpose,
      hint: hint?.trim() || undefined,
    },
  );
  return response.data;
}

export type SupportDeskClassifyPayload = {
  status?: string;
  priority?: string;
  assignedToUserId?: string;
  clearAssignee?: boolean;
  assignedTeamId?: string;
  clearTeam?: boolean;
  serviceCatalogItemId?: string;
  clearCatalogItem?: boolean;
  tags?: string;
  clearTags?: boolean;
  problemId?: string;
  clearProblem?: boolean;
};

export async function classifySupportDeskTicket(
  id: string,
  payload: SupportDeskClassifyPayload,
): Promise<ServiceTicket> {
  const response = await apiClient.patch<ServiceTicket>(
    `/v1/customer-success/desk/tickets/${id}/classify`,
    payload,
  );
  return response.data;
}

export type SupportDeskProblemTicket = {
  id: string;
  ticketCode: string;
  title: string;
  status: string;
  priority: string;
  companyName: string;
  createdAtUtc: string;
};

export type SupportDeskBroadcastPayload = {
  body?: string;
  useWorkaround?: boolean;
  onlyOpenTickets?: boolean;
  ticketIds?: string[];
};

export type SupportDeskBroadcastItem = {
  ticketId: string;
  ticketCode: string;
  outcome: string;
  errorMessage?: string | null;
};

export type SupportDeskBroadcastResult = {
  sentCount: number;
  skippedCount: number;
  failedCount: number;
  items: SupportDeskBroadcastItem[];
};

export async function listSupportDeskProblemTickets(
  problemId: string,
): Promise<SupportDeskProblemTicket[]> {
  const response = await apiClient.get<SupportDeskProblemTicket[]>(
    `/v1/customer-success/desk/problems/${problemId}/tickets`,
  );
  return response.data;
}

export async function broadcastSupportDeskProblemReply(
  problemId: string,
  payload: SupportDeskBroadcastPayload,
): Promise<SupportDeskBroadcastResult> {
  const response = await apiClient.post<SupportDeskBroadcastResult>(
    `/v1/customer-success/desk/problems/${problemId}/broadcast-reply`,
    payload,
  );
  return response.data;
}

export async function addSupportDeskComment(
  id: string,
  payload: { body: string; isInternal?: boolean; isHtml?: boolean; files?: File[] },
): Promise<void> {
  const files = payload.files ?? [];
  if (files.length === 0) {
    await apiClient.post(`/v1/customer-success/desk/tickets/${id}/comments`, {
      body: payload.body,
      isInternal: payload.isInternal ?? false,
      isHtml: payload.isHtml ?? false,
    });
    return;
  }

  const form = new FormData();
  form.append('body', payload.body);
  form.append('isInternal', String(payload.isInternal ?? false));
  form.append('isHtml', String(payload.isHtml ?? false));
  for (const file of files) {
    form.append('files', file);
  }

  await apiClient.post(`/v1/customer-success/desk/tickets/${id}/comments`, form);
}

export async function resendSupportDeskCommentOutbound(
  ticketId: string,
  commentId: string,
): Promise<void> {
  await apiClient.post(
    `/v1/customer-success/desk/tickets/${ticketId}/comments/${commentId}/resend-outbound`,
  );
}
